import type { ExecutionRecord, ExecutionSummary } from '@/services/api/retrostock-api'

/**
 * The summary a list surface should show for a run.
 *
 * The engine's `executionSummary` is right for cash-mode runs. For unit-mode
 * runs the engine ran every entry at a fixed unit share count against an
 * inflated starting balance, so its dollar P&L and drawdown are meaningless.
 * Profit factor is gross wins over gross losses in dollars, so a million
 * shares of a $12 stock outweighs a million shares of a $3 stock four to one
 * where cash mode weighted them equally: it is notional-weighted and does not
 * survive either (the 2026-09-05 smoke run read 0.04 against 1.68). Only win
 * rate and trade count hold. Until the Report has allocated the run and
 * written an `allocatedSummary` back onto the run config, the dollar-derived
 * fields read as unknown; once it has, they read from the allocation.
 *
 * See docs/superpowers/plans/2026-09-05-backtest-unit-mode-plan.md Task 3.6.
 */

type RunLike = Pick<ExecutionRecord, 'executionSummary' | 'runConfig'>

export function isUnitRun(run: RunLike | null | undefined): boolean {
  return run?.runConfig?.sizingMode === 'unit'
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

export function summaryForRun(run: RunLike | null | undefined): ExecutionSummary | null {
  const engine = run?.executionSummary ?? null
  if (!isUnitRun(run)) return engine

  const allocated = run?.runConfig?.allocatedSummary ?? null
  const base: ExecutionSummary = { ...(engine ?? {}) }
  if (allocated) {
    return {
      ...base,
      totalPnl: num(allocated.totalPnl),
      maxDrawdown: num(allocated.maxDrawdown),
      totalTrades: num(allocated.totalTrades) ?? base.totalTrades ?? null,
      winRate: num(allocated.winRate) ?? base.winRate ?? null,
      profitFactor: num(allocated.profitFactor),
    }
  }
  return { ...base, totalPnl: null, maxDrawdown: null, profitFactor: null }
}

/** Short label for a run's sizing, for badges beside the name or universe. */
export function sizingLabel(run: RunLike | null | undefined): 'Portfolio' | null {
  return isUnitRun(run) ? 'Portfolio' : null
}
